import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import {
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';
import { fetchCurrentUser } from '../../ducks/user.duck';

/**
 * Resolving the provider's single profile listing.
 *
 * A provider's public profile IS a listing — the Marketplace API has no
 * `users/query`, only `users/show`, so anything browsable has to be a listing.
 * That made "post a new listing" the front door to "create your provider
 * profile", and nothing stopped a provider walking through that door twice.
 * Two profiles in search, one set of user-level vetting documents behind them.
 *
 * This duck answers one question — does the current user already have a profile
 * listing, and which one is it? The route guard then either sends them into
 * that listing's wizard or lets a fresh draft be created.
 *
 * Note this is deliberately NOT `currentUserHasListings` from ducks/user.duck.js.
 * That flag queries `states: 'published'` and re-checks for drafts on purpose,
 * because the Topbar role heuristic and ModalMissingInformation both read it.
 * Widening it there would change two unrelated behaviours.
 */

// No `states` filter: ownListings.query returns every state (ManageListingsPage
// relies on the same thing to render drafts). A draft or a pendingApproval
// listing is an existing profile just as much as a published one — filtering
// them out would hand a second profile to any provider who abandoned the wizard
// halfway. perPage is generous only so the tie-break below sees the whole
// picture on legacy accounts that already collected duplicates.
const PROFILE_LISTING_QUERY_PARAMS = { page: 1, perPage: 100 };

// Which listing wins when a provider has more than one, best first.
//
// Ordered by how real the profile is, NOT by recency. The API returns
// createdAt-descending and the live dev account shows why that is the wrong
// default: an abandoned draft ("Kumasi Packaging Solutions", 15:35) sorts ahead
// of the provider's actual published profile ("Accra Food Safety Consulting
// Ltd", 15:04), so listings[0] walks them into the wrong one.
//
// Recency-of-edit would arguably be the friendlier rule, but the ownListing
// entity carries no `updatedAt` — the attributes are createdAt, state, deleted,
// title, description, price, geolocation, availabilityPlan and the data bags.
// So state rank is not just the safer rule, it is the only one available.
//
// `closed` is last but still INCLUDED: a provider who deactivated their profile
// and clicks through again reopens it, keeping the 22 vetting fields and the
// document slots keyed to their user. Dropping it here would silently start
// them over and leave the old profile stranded where only an operator sees it.
const PROFILE_STATE_RANK = [
  LISTING_STATE_PUBLISHED,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_DRAFT,
  LISTING_STATE_CLOSED,
];

const profileStateRank = listing => {
  const index = PROFILE_STATE_RANK.indexOf(listing?.attributes?.state);
  // An unknown state sorts last rather than first, so a future state added by
  // Sharetribe cannot quietly take over as somebody's profile.
  return index === -1 ? PROFILE_STATE_RANK.length : index;
};

/**
 * Pick the one listing that counts as *the* provider's profile.
 *
 * Called with every own listing, in every state, in the order the API returned
 * them (most recently created first). Returns the chosen listing, or undefined
 * when the provider has none yet and a fresh draft should be created.
 *
 * Each listing here is an ownListing entity:
 *   listing.id.uuid
 *   listing.attributes.title
 *   listing.attributes.state      'draft' | 'pendingApproval' | 'published' | 'closed'
 *   listing.attributes.createdAt
 *
 * @param {Array} listings every own listing of the current user
 * @returns {Object|undefined} the profile listing, or undefined to create one
 */
export const selectProfileListing = listings => {
  const candidates = (listings || []).filter(listing => listing?.attributes?.deleted !== true);

  return [...candidates].sort((a, b) => {
    const byState = profileStateRank(a) - profileStateRank(b);
    if (byState !== 0) {
      return byState;
    }
    // Same state: the newest, which matches the order the API already returned.
    return new Date(b?.attributes?.createdAt) - new Date(a?.attributes?.createdAt);
  })[0];
};

// ================ Async Thunks ================ //

const queryProfileListingPayloadCreator = (_, { extra: sdk, rejectWithValue }) =>
  sdk.ownListings
    .query(PROFILE_LISTING_QUERY_PARAMS)
    .then(response => {
      const listings = response.data.data || [];
      const profileListing = selectProfileListing(listings);

      // Only id, title and state are kept: the guard needs an id to route to,
      // a title to slug, and the state to choose the `draft` vs `edit` URL type
      // the same way ManageListingCard does. Holding entities here would
      // duplicate what ManageListingsPage already owns.
      return {
        profileListingId: profileListing?.id?.uuid || null,
        profileListingTitle: profileListing?.attributes?.title || null,
        profileListingState: profileListing?.attributes?.state || null,
      };
    })
    .catch(e => rejectWithValue(storableError(e)));

export const queryProfileListingThunk = createAsyncThunk(
  'app/NewListingPage/queryProfileListing',
  queryProfileListingPayloadCreator
);

export const queryProfileListing = () => dispatch =>
  dispatch(queryProfileListingThunk()).unwrap();

// ================ Slice ================ //

const newListingPageSlice = createSlice({
  name: 'app/NewListingPage',
  initialState: {
    // `queryResolved` is separate from `!queryInProgress` so the guard can tell
    // "finished, found nothing" apart from "not started yet" — the two want
    // opposite behaviour and both have a null listing id.
    queryInProgress: false,
    queryResolved: false,
    queryError: null,
    profileListingId: null,
    profileListingTitle: null,
    profileListingState: null,
  },
  reducers: {
    // Cleared when the guard unmounts. Route loadData runs *after* the first
    // render, so a second visit would otherwise redirect on the previous
    // visit's answer before the fresh query had a chance to land — stale by one
    // discarded draft.
    clearProfileListing: state => {
      state.queryInProgress = false;
      state.queryResolved = false;
      state.queryError = null;
      state.profileListingId = null;
      state.profileListingTitle = null;
      state.profileListingState = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(queryProfileListingThunk.pending, state => {
        state.queryInProgress = true;
        state.queryResolved = false;
        state.queryError = null;
      })
      .addCase(queryProfileListingThunk.fulfilled, (state, action) => {
        state.queryInProgress = false;
        state.queryResolved = true;
        state.profileListingId = action.payload.profileListingId;
        state.profileListingTitle = action.payload.profileListingTitle;
        state.profileListingState = action.payload.profileListingState;
      })
      .addCase(queryProfileListingThunk.rejected, (state, action) => {
        state.queryInProgress = false;
        state.queryResolved = true;
        state.queryError = action.payload;
      });
  },
});

export const { clearProfileListing } = newListingPageSlice.actions;

export default newListingPageSlice.reducer;

// ================ loadData ================ //

export const loadData = () => dispatch =>
  Promise.all([dispatch(fetchCurrentUser()), dispatch(queryProfileListing())]);
